import { Link, useParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import NotFound from "@/pages/NotFound";
import { paidGuides, guidePurchaseUrls } from "@/mocks/guides";

export default function GuideDetail() {
  const { slug } = useParams();
  const guide = paidGuides.find((item) => item.slug === slug);

  if (!guide) {
    return <NotFound />;
  }

  const purchaseUrl = guidePurchaseUrls[guide.slug] ?? null;

  return (
    <div className="flex min-h-screen w-full flex-col bg-background-50">
      <SiteHeader />
      <main className="w-full flex-1">
        <div className="w-full border-b border-background-200 bg-background-100">
          <div className="mx-auto w-full max-w-content px-5 py-10 md:px-6 md:py-14">
            <nav
              aria-label="パンくずリスト"
              className="flex flex-wrap items-center gap-2 text-[11.5px] text-foreground-500"
            >
              <Link to="/" className="transition-colors hover:text-primary-700">
                ホーム
              </Link>
              <i className="ri-arrow-right-s-line text-[13px]" />
              <Link to="/guides" className="transition-colors hover:text-primary-700">
                有料ガイド
              </Link>
              <i className="ri-arrow-right-s-line text-[13px]" />
              <span className="text-foreground-700">GUIDE {guide.number}</span>
            </nav>

            <p className="mt-6 inline-flex items-center gap-2 rounded-full bg-accent-100 px-3.5 py-1 text-[11px] font-semibold text-accent-800">
              <i className="ri-lock-line text-[12px]" />
              有料ガイド（準備中）
            </p>
            <h1 className="mt-4 font-heading text-[26px] font-bold leading-[1.5] tracking-tight text-foreground-950 md:text-[34px]">
              {guide.title}
            </h1>
            <p className="mt-4 max-w-2xl text-[13px] leading-[2] text-foreground-700 md:text-[14px]">
              {guide.summary}
            </p>
          </div>
        </div>

        <div className="mx-auto w-full max-w-content px-5 py-12 md:px-6 md:py-16">
          <div className="grid gap-10 lg:grid-cols-[minmax(0,1fr)_320px] lg:gap-12">
            <div className="min-w-0">
              <div className="flex items-start gap-3 rounded-[10px] border border-dashed border-background-300 bg-background-100 px-5 py-4">
                <i className="ri-time-line mt-0.5 shrink-0 text-[17px] text-accent-600" />
                <div>
                  <p className="font-heading text-[13.5px] font-bold text-foreground-950">
                    このガイドは現在制作中です
                  </p>
                  <p className="mt-1.5 text-[12.5px] leading-[1.9] text-foreground-600">
                    完成した商品ではありません。掲載しているのは予定している内容の一覧で、購入はできません。価格・販売開始時期は未定です。
                  </p>
                </div>
              </div>

              <h2 className="mt-10 flex items-start gap-3 font-heading text-[19px] font-bold leading-[1.6] text-foreground-950">
                <span className="mt-[9px] h-[3px] w-6 shrink-0 rounded-full bg-accent-500" />
                収録予定の内容
              </h2>
              <ul className="mt-5 space-y-3">
                {guide.contents.map((item) => (
                  <li
                    key={item}
                    className="flex items-start gap-2.5 rounded-[8px] border border-background-200 bg-background-50 px-4 py-3 text-[13px] leading-[1.9] text-foreground-700"
                  >
                    <i className="ri-check-line mt-[5px] shrink-0 text-[15px] text-accent-500" />
                    {item}
                  </li>
                ))}
              </ul>

              <h2 className="mt-10 flex items-start gap-3 font-heading text-[19px] font-bold leading-[1.6] text-foreground-950">
                <span className="mt-[9px] h-[3px] w-6 shrink-0 rounded-full bg-accent-500" />
                無料記事との違い
              </h2>
              <div className="mt-5 space-y-3 text-[13px] leading-[1.95] text-foreground-700">
                <p>
                  無料記事では、制度の基本（仕組み・流れ・用語）と公式情報へのリンクを公開しています。誰でも読める一般公開の内容です。
                </p>
                <p>
                  有料PDFガイドでは、運営者の実体験にもとづく手順や記録、オリジナルのチェックリスト・テンプレート、独自に作成した練習問題など、追加の価値をまとめる予定です。
                </p>
              </div>

              <div className="mt-6 flex items-start gap-3 rounded-[10px] border border-background-200 bg-background-50 px-5 py-4">
                <i className="ri-shield-check-line mt-0.5 shrink-0 text-[16px] text-primary-700" />
                <p className="text-[12px] leading-[1.95] text-foreground-600">{guide.note}</p>
              </div>

              <div className="mt-6 flex items-start gap-3 rounded-[10px] border border-background-200 bg-background-50 px-5 py-4">
                <i className="ri-copyright-line mt-0.5 shrink-0 text-[16px] text-foreground-500" />
                <p className="text-[12px] leading-[1.95] text-foreground-600">
                  第三者が作成した資料（公式の問題・書類・画像・他サイトの記事など）は、転載・再配布が明確に許可されているものを除き使用しません。ガイド内の文章・図表・練習問題は独自に作成し、引用する場合は出典と利用条件を記録します。
                </p>
              </div>
            </div>

            <aside className="w-full lg:sticky lg:top-[92px] lg:self-start">
              <div className="rounded-[10px] border border-background-200 bg-background-50 p-5">
                <p className="font-heading text-[13px] font-bold text-foreground-950">販売について</p>
                <dl className="mt-3.5 space-y-2.5 text-[12px]">
                  <div className="flex items-center justify-between gap-3">
                    <dt className="text-foreground-500">形式</dt>
                    <dd className="font-medium text-foreground-800">PDF（予定）</dd>
                  </div>
                  <div className="flex items-center justify-between gap-3">
                    <dt className="text-foreground-500">価格</dt>
                    <dd className="font-medium text-foreground-800">未定</dd>
                  </div>
                  <div className="flex items-center justify-between gap-3">
                    <dt className="text-foreground-500">販売方法</dt>
                    <dd className="text-right font-medium text-foreground-800">
                      外部販売サービス（note等）を予定
                    </dd>
                  </div>
                  <div className="flex items-center justify-between gap-3">
                    <dt className="text-foreground-500">状態</dt>
                    <dd className="font-semibold text-accent-700">準備中</dd>
                  </div>
                </dl>

                {purchaseUrl ? (
                  <a
                    href={purchaseUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-5 inline-flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-full bg-accent-500 px-5 py-3 text-[12.5px] font-semibold text-background-50 transition-colors hover:bg-accent-600"
                  >
                    購入ページへ進む
                    <i className="ri-external-link-line text-[13px]" />
                  </a>
                ) : (
                  <button
                    type="button"
                    disabled
                    className="mt-5 inline-flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-full border border-background-300 bg-background-100 px-5 py-3 text-[12.5px] font-semibold text-foreground-400"
                  >
                    <i className="ri-lock-line text-[13px]" />
                    準備中のため購入できません
                  </button>
                )}
                <p className="mt-2.5 text-[11px] leading-[1.8] text-foreground-500">
                  完成後に購入ページへのリンクを設定します。
                </p>
              </div>

              <div className="mt-4 rounded-[10px] border border-primary-200 bg-primary-50 p-5">
                <p className="font-heading text-[13px] font-bold text-primary-900">無料記事も公開中</p>
                <p className="mt-2 text-[12px] leading-[1.85] text-foreground-700">
                  制度の基本は、すべて無料で読めます。
                </p>
                <Link
                  to="/learn"
                  className="mt-3.5 inline-flex items-center gap-1.5 text-[12px] font-semibold text-primary-800 transition-colors hover:text-primary-900"
                >
                  無料で学ぶ
                  <i className="ri-arrow-right-line text-[13px]" />
                </Link>
              </div>
            </aside>
          </div>

          <div className="mt-14 border-t border-background-200 pt-8">
            <Link
              to="/guides"
              className="inline-flex items-center gap-2 whitespace-nowrap rounded-full border border-background-300 bg-background-50 px-5 py-2.5 text-[12.5px] font-semibold text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-800"
            >
              <i className="ri-arrow-left-line text-[14px]" />
              有料ガイド一覧に戻る
            </Link>
          </div>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}